import { Button } from "@aurumiq/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@aurumiq/ui/components/card";
import { CopyField } from "@aurumiq/ui/composite/copy-button";
import { KpiTile } from "@aurumiq/ui/composite/kpi-tile";
import { ReasonDialog } from "@aurumiq/ui/composite/reason-dialog";
import { StatusTag, statusVariant } from "@aurumiq/ui/composite/status-tag";
import { dmy, dmyTime } from "@aurumiq/ui/lib/format";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { usePermission } from "@/auth/use-permission";
import { SelectField, TextAreaField, TextField } from "@/components/field";
import { QueryState, Stat, StatList } from "@/components/query-state";
import { COUNTRIES } from "@/lib/countries";
import type { OrgDetail, OrgStatus } from "@/lib/types";
import { useOrg, usePatchOrg } from "./queries";

const STATUSES: OrgStatus[] = ["trial", "active", "suspended", "cancelled"];
const NEEDS_REASON: OrgStatus[] = ["suspended", "cancelled"];

const schema = z.object({
  name: z.string().trim().min(2, "Name is required"),
  country: z.string(),
  status: z.enum(STATUSES),
  verify_base_url: z.union([z.literal(""), z.url("Must be a valid URL")]),
  notes: z.string(),
});
type FormValues = z.infer<typeof schema>;

function toValues(o: OrgDetail): FormValues {
  return {
    name: o.name,
    country: o.country ?? "",
    status: o.status,
    verify_base_url: o.settings.verify_base_url ?? "",
    notes: o.settings.notes ?? "",
  };
}

export function OverviewTab({ orgId }: { orgId: string }) {
  const org = useOrg(orgId);
  return (
    <QueryState query={org} rows={6}>
      {(o) => <OverviewLoaded org={o} />}
    </QueryState>
  );
}

function OverviewLoaded({ org }: { org: OrgDetail }) {
  const canWrite = usePermission("orgs:write");
  const patch = usePatchOrg(org.id);
  const [pending, setPending] = useState<FormValues | null>(null);
  const form = useForm<FormValues>({ resolver: zodResolver(schema), values: toValues(org) });
  const err = (k: keyof FormValues) => form.formState.errors[k]?.message;

  const save = (v: FormValues, reason?: string) =>
    patch.mutateAsync({
      name: v.name,
      country: v.country || undefined,
      status: v.status,
      verify_base_url: v.verify_base_url,
      notes: v.notes,
      reason,
    });

  const onSubmit = form.handleSubmit((v) => {
    if (v.status !== org.status && NEEDS_REASON.includes(v.status)) {
      setPending(v);
      return;
    }
    return save(v);
  });

  const sp = org.settings.seller_public;
  const activeDevices = org.devices.filter((d) => d.status === "active").length;

  return (
    <div className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <KpiTile label="Active devices / seats" value={`${activeDevices} / ${org.seats}`} />
        <KpiTile
          label="Plan"
          value={org.subscription?.plan_name ?? org.plan_code}
          hint={org.subscription ? org.subscription.status : "no subscription"}
        />
        <KpiTile
          label="Subscribed until"
          value={org.subscription?.sub_until ? dmy(org.subscription.sub_until) : "—"}
        />
        <KpiTile
          label="Created"
          value={dmy(org.created_at)}
          hint={`updated ${dmy(org.updated_at)}`}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[3fr_2fr]">
        <Card>
          <CardHeader>
            <CardTitle>Organisation</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-3">
              <div>
                <span className="mb-1 block font-semibold text-muted-foreground text-xs">Code</span>
                <CopyField value={org.code} />
              </div>
              <TextField
                label="Name"
                error={err("name")}
                disabled={!canWrite}
                {...form.register("name")}
              />
              <div className="grid gap-3 sm:grid-cols-2">
                <SelectField label="Country" disabled={!canWrite} {...form.register("country")}>
                  <option value="">—</option>
                  {COUNTRIES.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code} · {c.name}
                    </option>
                  ))}
                </SelectField>
                <SelectField
                  label="Status"
                  disabled={!canWrite}
                  hint="Suspending or cancelling asks for a reason."
                  {...form.register("status")}
                >
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </SelectField>
              </div>
              <TextField
                label="Verify base URL"
                placeholder="https://verify.example.com"
                error={err("verify_base_url")}
                disabled={!canWrite}
                {...form.register("verify_base_url")}
              />
              <TextAreaField label="Notes" disabled={!canWrite} {...form.register("notes")} />
              {canWrite ? (
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => form.reset(toValues(org))}
                    disabled={!form.formState.isDirty || patch.isPending}
                  >
                    Reset
                  </Button>
                  <Button type="submit" disabled={!form.formState.isDirty || patch.isPending}>
                    {patch.isPending ? "Saving…" : "Save changes"}
                  </Button>
                </div>
              ) : null}
            </form>
          </CardContent>
        </Card>

        <div className="grid content-start gap-4">
          <Card>
            <CardHeader>
              <CardTitle>Seller public profile</CardTitle>
            </CardHeader>
            <CardContent>
              {sp ? (
                <StatList>
                  <Stat label="Name">{sp.name ?? "—"}</Stat>
                  <Stat label="Arabic name">
                    <span dir="rtl">{sp.name_ar ?? "—"}</span>
                  </Stat>
                  <Stat label="TRN">
                    <span className="font-mono">{sp.trn ?? "—"}</span>
                  </Stat>
                  <Stat label="Licence no.">{sp.license_no ?? "—"}</Stat>
                  <Stat label="Address">{sp.address ?? "—"}</Stat>
                  <Stat label="Phone">{sp.phone ?? "—"}</Stat>
                  <Stat label="Email">{sp.email ?? "—"}</Stat>
                  <Stat label="Website">{sp.website ?? "—"}</Stat>
                </StatList>
              ) : (
                <p className="text-muted-foreground text-sm">
                  Not set. The customer fills this in from the desktop app settings.
                </p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Signing secrets</CardTitle>
            </CardHeader>
            <CardContent>
              {org.secrets.length ? (
                <ul className="grid gap-1.5 text-sm">
                  {org.secrets.map((s) => (
                    <li key={s.version} className="flex items-center gap-2">
                      <span className="font-mono">v{s.version}</span>
                      <StatusTag variant={statusVariant(s.status)}>
                        {s.status.toUpperCase()}
                      </StatusTag>
                      <span className="ml-auto text-muted-foreground text-xs">
                        {dmyTime(s.created_at)}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">No secret versions yet.</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <ReasonDialog
        open={pending !== null}
        onOpenChange={(o) => {
          if (!o) setPending(null);
        }}
        title={`Set status to ${pending?.status}?`}
        description="Devices lose access once the change propagates. The reason is written to the audit log."
        confirmLabel={`Mark ${pending?.status}`}
        onConfirm={async (reason) => {
          if (pending) await save(pending, reason);
        }}
      />
    </div>
  );
}
